import { useCallback, useEffect, useState } from "react";
import { Check, FolderKanban, Mail, MessageSquare, Plus, Send, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PEOPLE, spaceOf } from "@/lib/spaces";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { fmtDate, fromNow } from "@/lib/time";
import { priorityOf, scheduleText } from "@/lib/priority";
import { SpaceAvatar } from "@/components/SpaceAvatar";

const IDLE_CLOSE_MS = 90000;

const Overview = ({ rid, stamp }) => {
  const [d, setD] = useState(undefined);
  useEffect(() => {
    api.get(`/ai/reminders/${rid}/overview`, { timeout: 60000 }).then((r) => setD(r.data)).catch(() => setD(null));
  }, [rid, stamp]);
  if (d === null || d?.overview === null) return null;
  return (
    <div className="rounded-[0.8em] border border-cyan-400/30 bg-cyan-400/[0.07] p-[0.9em]" data-testid="wall-detail-ai">
      <p className="flex items-center gap-[0.4em] text-[0.65em] font-semibold uppercase tracking-wider text-cyan-300"><Sparkles className="h-[1.1em] w-[1.1em]" /> AI overview</p>
      {d === undefined ? <div className="mt-[0.5em] h-[2.6em] animate-pulse rounded bg-white/5" /> : (
        <>
          <p className="mt-[0.4em] text-[0.95em] leading-snug" data-testid="wall-detail-ai-text">{d.overview}</p>
          {d.next && <p className="mt-[0.4em] text-[0.85em]"><b className="text-cyan-200">Next:</b> {d.next}</p>}
        </>
      )}
    </div>
  );
};

const Fact = ({ label, children }) => (
  <div><p className="text-[0.6em] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-[0.15em] text-[0.85em]">{children}</p></div>
);

const WhoPicker = ({ who, setWho }) => (
  <div className="flex flex-wrap items-center gap-[0.4em] text-[0.7em]" data-testid="wall-who-picker">
    <span className="text-muted-foreground">You are:</span>
    {PEOPLE.map((p) => (
      <button key={p.key} type="button" onClick={() => setWho(p.key)} data-testid={`wall-who-${p.key}`}
        className="rounded-full border px-[0.8em] py-[0.2em] font-semibold transition-colors" style={who === p.key ? { background: p.color, color: "#000", borderColor: p.color } : { color: p.color }}>{p.name}</button>
    ))}
  </div>
);

const WallUpdate = ({ rid, who, onDone }) => {
  const [text, setText] = useState("");
  const post = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/reminders/${rid}/updates`, { body: text, as_user: who || undefined });
      setText("");
      toast.success("Update added");
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={post} className="flex items-end gap-[0.5em]" data-testid="wall-update-form">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Add an update…" data-testid="wall-update-input"
        className="min-h-[2.8em] flex-1 resize-y rounded-[0.6em] border bg-background/60 px-[0.8em] py-[0.5em] text-[0.9em] outline-none focus:border-white/40" />
      <button disabled={!text.trim()} className="flex h-[2.6em] items-center gap-[0.4em] rounded-full bg-primary px-[1em] text-[0.8em] font-semibold text-primary-foreground disabled:opacity-50" data-testid="wall-update-submit"><Send className="h-[1em] w-[1em]" /> Post</button>
    </form>
  );
};

const WallChecklist = ({ r, onSaved }) => {
  const [text, setText] = useState("");
  const items = r.checklist || [];
  const save = (checklist) => api.put(`/reminders/${r.id}/checklist`, { checklist }).then(onSaved).catch((e) => toast.error(errMsg(e)));
  return (
    <div data-testid="wall-detail-checklist">
      <p className="text-[0.65em] font-semibold uppercase tracking-wider text-muted-foreground">Checklist{items.length ? ` · ${items.filter((c) => c.done).length}/${items.length}` : ""}</p>
      <ul className="mt-[0.4em] space-y-[0.3em]">
        {items.map((c, i) => (
          <li key={c.id || i} className="group flex items-center gap-[0.5em] text-[0.95em]">
            <button onClick={() => save(items.map((x, j) => (j === i ? { ...x, done: !x.done } : x)))} data-testid={`wall-checklist-toggle-${i}`}
              className={`flex h-[1.1em] w-[1.1em] shrink-0 items-center justify-center rounded border ${c.done ? "border-emerald-400 bg-emerald-400 text-black" : "border-white/30"}`}>{c.done && <Check className="h-[0.75em] w-[0.75em]" />}</button>
            <span className={`flex-1 ${c.done ? "text-muted-foreground line-through" : ""}`}>{c.text}</span>
            <button onClick={() => save(items.filter((_, j) => j !== i))} className="opacity-0 group-hover:opacity-100" data-testid={`wall-checklist-remove-${i}`}><X className="h-[0.9em] w-[0.9em] text-muted-foreground" /></button>
          </li>
        ))}
      </ul>
      <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) { save([...items, { text: text.trim(), done: false }]); setText(""); } }} className="mt-[0.4em] flex items-center gap-[0.5em]">
        <Plus className="h-[0.9em] w-[0.9em] text-muted-foreground" />
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a checklist step" className="h-[2em] flex-1 bg-transparent text-[0.9em] outline-none" data-testid="wall-checklist-input" />
      </form>
    </div>
  );
};

export const WallDetail = ({ rid, scale, onClose }) => {
  const [r, setR] = useState(null);
  const { user } = useAuth();
  const { celebrate } = useSettings();
  const screen = user?.role === "screen";
  const [who, setWho] = useState(() => (screen ? localStorage.getItem("wb-who") || "" : ""));
  const pickWho = (k) => { setWho(k); localStorage.setItem("wb-who", k); };
  const load = useCallback(() => api.get(`/reminders/${rid}`).then((x) => setR(x.data)).catch(onClose), [rid, onClose]);
  const complete = async () => {
    try {
      await api.post(`/reminders/${rid}/complete`, { as_user: who || undefined });
      celebrate(r.title);
      toast.success("Marked done");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => {
    load();
    const poll = setInterval(load, 30000);
    let idle = setTimeout(onClose, IDLE_CLOSE_MS);
    const reset = () => { clearTimeout(idle); idle = setTimeout(onClose, IDLE_CLOSE_MS); };
    const key = (e) => (e.key === "Escape" ? onClose() : reset());
    window.addEventListener("pointermove", reset);
    window.addEventListener("keydown", key);
    return () => { clearInterval(poll); clearTimeout(idle); window.removeEventListener("pointermove", reset); window.removeEventListener("keydown", key); };
  }, [load, onClose]);

  const p = priorityOf(r?.priority);
  const s = spaceOf(r?.space);
  const updates = [...(r?.updates || [])].reverse();
  const stamp = r ? `${updates.length}-${r.details}-${r.status}` : "";
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" onClick={onClose} data-testid="wall-detail-backdrop" />
      <aside className="fade-up fixed inset-y-0 right-0 z-50 w-full overflow-y-auto border-l bg-card p-[1.4em] shadow-2xl md:w-1/2" style={{ fontSize: `${scale * 16}px`, boxShadow: `inset 0.25em 0 0 ${s.color}` }} data-testid="wall-detail-panel">
        <button onClick={onClose} className="absolute right-[1em] top-[1em] flex h-[2.2em] w-[2.2em] items-center justify-center rounded-full border transition-colors hover:bg-white/5" data-testid="wall-detail-close"><X className="h-[1.1em] w-[1.1em]" /></button>
        {!r ? <div className="mt-[3em] h-[8em] animate-pulse rounded-xl bg-white/[0.04]" /> : (
          <div className="space-y-[1.2em] pr-[2.5em]">
            <div>
              <div className="flex flex-wrap items-center gap-[0.6em] text-[0.75em]">
                <span className="flex items-center gap-[0.4em] rounded-full px-[0.8em] py-[0.25em] font-semibold" style={{ background: `${p.color}1f`, color: p.color }} data-testid="wall-detail-priority"><span className="h-[0.6em] w-[0.6em] rounded-full" style={{ background: p.color }} /> {scheduleText(r)}</span>
                <span className="flex items-center gap-[0.4em] font-semibold" style={{ color: s.color }}><SpaceAvatar space={r.space} /> {s.name}</span>
                {(r.assignees || []).map((a) => <span key={a} className="flex items-center gap-[0.3em] font-semibold" style={{ color: spaceOf(a).color }}><SpaceAvatar space={a} /> {spaceOf(a).name}</span>)}
                {r.silent && <span className="font-semibold text-amber-200">No emails</span>}
                {r.status === "done" && <span className="flex items-center gap-1 font-semibold text-emerald-300"><Check className="h-[1em] w-[1em]" /> Done</span>}
              </div>
              <h2 className="mt-[0.4em] font-display text-[2em] font-bold leading-tight tracking-tight" data-testid="wall-detail-title">{r.title}</h2>
              {r.details && <p className="mt-[0.5em] whitespace-pre-wrap text-[1em] text-foreground/85" data-testid="wall-detail-details">{r.details}</p>}
            </div>
            <Overview rid={r.id} stamp={stamp} />
            {screen && <WhoPicker who={who} setWho={pickWho} />}
            {r.status !== "done" && (
              <button onClick={complete} disabled={screen && !who} className="flex items-center gap-[0.5em] rounded-full bg-emerald-400 px-[1.2em] py-[0.5em] text-[0.85em] font-bold text-black transition-colors hover:bg-emerald-300 disabled:opacity-40" data-testid="wall-detail-complete">
                <Check className="h-[1em] w-[1em]" /> Mark done{screen && !who ? " (pick who you are)" : ""}
              </button>
            )}
            <div className="grid grid-cols-2 gap-[0.9em] rounded-[0.8em] border bg-background/40 p-[0.9em] lg:grid-cols-4">
              <Fact label="Next email">{r.status === "done" ? "Done" : r.next_fire_at ? fmtDate(r.next_fire_at) : "Sent"}</Fact>
              <Fact label="Emails sent"><span className="flex items-center gap-1 font-mono"><Mail className="h-[0.9em] w-[0.9em]" /> {r.sent_count || 0}</span></Fact>
              <Fact label="Set by">{spaceOf(r.created_by).name} · {fromNow(r.created_at)}</Fact>
              <Fact label="Project">{r.project_title ? <span className="flex items-center gap-1"><FolderKanban className="h-[0.9em] w-[0.9em]" /> {r.project_title}</span> : "–"}</Fact>
            </div>
            <WallChecklist r={r} onSaved={load} />
            <div data-testid="wall-detail-updates">
              <p className="flex items-center gap-[0.4em] text-[0.65em] font-semibold uppercase tracking-wider text-muted-foreground"><MessageSquare className="h-[1.1em] w-[1.1em]" /> Updates · {updates.length}</p>
              <div className="mt-[0.5em]">{screen && !who ? <p className="text-[0.8em] text-amber-200">Pick who you are above to post an update.</p> : <WallUpdate rid={r.id} who={who} onDone={load} />}</div>
              {!updates.length && <p className="mt-[0.4em] text-[0.9em] text-muted-foreground">No updates yet.</p>}
              <div className="mt-[0.5em] space-y-[0.8em]">
                {updates.map((u) => (
                  <div key={u.id} className="border-l-[0.2em] pl-[0.8em]" style={{ borderColor: spaceOf(u.created_by).color }} data-testid={`wall-detail-update-${u.id}`}>
                    <p className="text-[0.75em] text-muted-foreground"><span className="font-semibold text-foreground">{spaceOf(u.created_by).name}</span> · {fmtDate(u.created_at)} · {fromNow(u.created_at)}</p>
                    <p className="mt-[0.15em] whitespace-pre-wrap text-[1.05em] leading-snug">{u.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};
